/**
 * Risk-based MFA policy.
 *
 * MFA enrollment is REQUIRED for every role that can reach protected health
 * information beyond a single patient's own record — the workforce roles
 * (professional/provider, facility, admin, and any privileged manager role).
 * A compromised workforce account exposes many patients' PHI, so that is where
 * the second factor matters most.
 *
 * It is NOT forced on `patient` accounts. A patient reaches only their OWN
 * record (lower-risk profile), and forcing MFA on public sign-ups harms
 * adoption. This is a deliberate, documented risk-based decision: the current
 * HIPAA Security Rule (§164.312(d)) requires authentication but does not
 * mandate MFA specifically. Patients may still enroll voluntarily.
 *
 * NOTE: this is a "for now" posture. The Jan 2025 Security Rule NPRM, if
 * finalized as written, would require MFA for ALL systems that access ePHI —
 * including patient-facing access — and remove the addressable distinction.
 * Revisit patient exemption when that rule lands.
 *
 * Fail closed: anything not explicitly exempt requires MFA, so a privileged
 * role added later is protected by default rather than silently exempt.
 */

/** Roles that do NOT require forced MFA enrollment. Keep this list tiny and deliberate. */
// Learners only reach MorningReport's simulated teaching cases, which hold no PHI.
export const MFA_EXEMPT_ROLES = ["patient", "learner"];

/* ============================================================================
 * ██  TODO: RE-ENABLE MFA BEFORE ANY REAL PHI / GENERAL AVAILABILITY  ██
 * ============================================================================
 *
 * !!! TEMP KILL SWITCH — MFA ENROLLMENT IS DISABLED FOR EVERYONE, EVERYWHERE !!!
 *
 * Set 2026-07 to unblock the beta/demo. While MFA_ENFORCEMENT_DISABLED === true:
 *   - roleRequiresMfa() ALWAYS returns false
 *   - NO role (provider, admin, facility, manager) is forced to enroll MFA
 *   - in ANY environment: localhost, sandbox, staging, AND production
 *
 * This is a KNOWN HIPAA access-control GAP. Workforce accounts reach many
 * patients' PHI; leaving them without a second factor is not an acceptable
 * long-term posture (see the risk-based policy documented above).
 *
 * TO RESTORE:
 *   1. Set MFA_ENFORCEMENT_DISABLED = false (re-enables the risk-based policy).
 *   2. ALSO reconcile production's separate prod-only kill switch (commit
 *      80484bad) — flip BOTH or MFA stays off in prod.
 *   3. Confirm the deployed-env enforcement (shouldSkipMfaEnrollmentUi) is on.
 *
 * DO NOT SHIP TO GENERAL AVAILABILITY WITH THIS SET TO true.
 * ============================================================================ */
const MFA_ENFORCEMENT_DISABLED = true;

/**
 * @param {string|undefined|null} role - The user's role from their user doc.
 * @returns {boolean} true when this role must enroll MFA before accessing the app.
 */
export const roleRequiresMfa = (role) =>
  !MFA_ENFORCEMENT_DISABLED && !MFA_EXEMPT_ROLES.includes(role);
